import { FolderTree, Smartphone, AlertCircle, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Flight Record hints component.
 *
 * Shows the precise folder paths for DJI Fly / DJI Pilot / DJI Pilot 2
 * so the pilot knows exactly WHERE to navigate when the system folder
 * picker opens. Addresses Android scoped storage (Android 11+) by
 * recommending picking the /sdcard/DJI/ parent - the scanner walks
 * recursively up to 4 levels deep.
 */
export default function FlightRecordHints() {
  const copy = async (txt) => {
    try {
      if (!navigator.clipboard?.writeText) {
        toast.error('Clipboard nao suportado - copie manualmente');
        return;
      }
      await navigator.clipboard.writeText(txt);
      toast.success('Caminho copiado');
    } catch {
      toast.error('Falha ao copiar');
    }
  };

  return (
    <div className="fs-card" data-testid="fs-flightrecord-hints" style={{ borderColor: 'rgba(124,58,237,0.3)' }}>
      <div className="fs-card-title" style={{ color: '#a78bfa' }}>
        <FolderTree size={12} /> DICA · PASTA FLIGHTRECORD
      </div>

      <p style={{ fontSize: 12, color: 'var(--fs-text-dim)', margin: '0 0 12px 0', lineHeight: 1.6 }}>
        Os apps da DJI salvam os logs de voo em uma pasta chamada <strong>FlightRecord</strong>.
        No menu do seletor de pasta, navegue ate um dos caminhos abaixo e toque em <strong>Usar esta pasta</strong>.
      </p>

      <PathRow
        app="DJI Fly"
        sub="Mavic 3 / Air 2S / Mini 3/4 / Avata"
        path="/sdcard/DJI/dji.go.v5/FlightRecord/"
        onCopy={copy}
      />
      <PathRow
        app="DJI Pilot 2"
        sub="Mavic 3 Enterprise / Matrice 30, 300, 350, 3D, 3T"
        path="/sdcard/DJI/dji.pilot2/FlightRecord/"
        onCopy={copy}
      />
      <PathRow
        app="DJI Pilot"
        sub="Phantom 4 RTK / Matrice 200 / modelos antigos"
        path="/sdcard/DJI/dji.pilot/FlightRecord/"
        onCopy={copy}
      />

      <div style={{
        marginTop: 12, padding: 10, borderRadius: 8,
        background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)',
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'start' }}>
          <CheckCircle2 size={14} style={{ color: 'var(--fs-cyan)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ fontSize: 11, fontWeight: 600, margin: '0 0 4px 0', color: 'var(--fs-cyan)' }}>
              RECOMENDADO · Selecione a pasta RAIZ
            </p>
            <p style={{ fontSize: 11, color: 'var(--fs-text-dim)', margin: 0, lineHeight: 1.5 }}>
              Se voce usar mais de um app DJI, selecione <code>/sdcard/DJI/</code>
              {' '}como raiz. O FlightSync varre automaticamente ate 4 niveis e encontra TODAS as pastas <code>FlightRecord</code> dentro.
            </p>
          </div>
        </div>
      </div>

      <div style={{
        marginTop: 8, padding: 10, borderRadius: 8,
        background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)',
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'start' }}>
          <AlertCircle size={14} style={{ color: 'var(--fs-amber)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ fontSize: 11, fontWeight: 600, margin: '0 0 4px 0', color: 'var(--fs-amber)' }}>
              ANDROID 11+ · Storage escopado
            </p>
            <p style={{ fontSize: 11, color: 'var(--fs-text-dim)', margin: 0, lineHeight: 1.5 }}>
              Em DJI RC Pro / RC 2 / RC Plus (Android 11+) a pasta <code>/Android/data/</code> e protegida.
              Use sempre o caminho <strong>/sdcard/DJI/</strong> (publico) — os apps DJI mantem uma copia automatica la.
            </p>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--fs-text-dim)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--fs-text)' }}>Arquivos suportados na pasta:</strong>
        <br />
        <Smartphone size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />
        <code>DJIFlightRecord_*.txt</code> · <code>*.csv</code> · <code>*.dat</code>
      </div>
    </div>
  );
}

function PathRow({ app, sub, path, onCopy }) {
  return (
    <div
      style={{
        padding: 10, borderRadius: 8,
        background: 'rgba(5,6,13,0.5)', border: '1px solid var(--fs-border)',
        marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10,
      }}
      data-testid={`fs-fr-path-${app.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fs-cyan)' }}>
          {app} <span style={{ fontSize: 10, color: 'var(--fs-text-dim)', fontWeight: 400 }}>· {sub}</span>
        </div>
        <code style={{
          fontSize: 11, color: 'var(--fs-text)', display: 'block',
          fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all',
        }}>
          {path}
        </code>
      </div>
      <button
        onClick={() => onCopy(path)}
        style={{
          background: 'transparent', border: '1px solid var(--fs-border)',
          color: 'var(--fs-cyan)', padding: 6, borderRadius: 6, cursor: 'pointer',
        }}
        title="Copiar caminho"
      >
        <Copy size={12} />
      </button>
    </div>
  );
}
